/**
 * Answer library (คลังคำตอบ, "/" in the composer) + image/video library (คลังรูป·วิดีโอ) — docs/library.md.
 * Kept out of the inbox store: the composer asks this store for the "/" list, the files staged for the next
 * message and uploads; the config sections use it for editing.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getApi } from '../api'
import { ApiError } from '../api/client'
import type {
  AiIntent,
  AnswerTypeInfo,
  CannedInput,
  CannedItem,
  MediaAsset,
  MediaFolder,
  MediaPatch,
  Placeholder,
} from '../api/types'
import { pickerList } from '../lib/libraryView'
import { prepareFile, type PreparedFile } from '../lib/media'

/**
 * The files an answer really sends: /menu sends the poster of the approved "เมนูรอบนี้" (`send_media`) instead of
 * its own pictures while that round is in use; every other answer sends its own `media`.
 */
export function sendMediaOf(item: Pick<CannedItem, 'media' | 'send_media'>): MediaAsset[] {
  return item.send_media ?? item.media
}

/** A library file waiting in the composer to go out with the next message. */
export interface StagedFile {
  key: string
  asset: MediaAsset
  /**
   * manual = picked/uploaded · canned = came with a "/" answer or a button · quick = Jev's quick answer ·
   * draft = rides along with the draft (`ai.media`, e.g. the approved menu round's poster)
   */
  source: 'manual' | 'canned' | 'quick' | 'draft'
}

export interface UploadJob {
  id: string
  name: string
  kind: 'image' | 'video' | null
  bytes: number
  /** 0..1 */
  progress: number
  status: 'preparing' | 'uploading' | 'saving' | 'done' | 'error'
  error: string | null
  warnings: string[]
  /** Blob URL shown while the upload runs. */
  thumb: string | null
  asset: MediaAsset | null
  /** Chat this upload goes to (staged there when done), or null for the library screen. */
  convId: string | null
}

/** Uploads started from a chat land in this folder so the library stays tidy. */
export const CHAT_FOLDER = 'อัปโหลดจากแชท'

const errText = (e: unknown): string =>
  e instanceof ApiError || e instanceof Error ? e.message : 'เกิดข้อผิดพลาด ลองใหม่อีกครั้ง'

let jobSeq = 0

export const useLibrary = defineStore('library', () => {
  const api = () => getApi()

  // ------------------------------------------------ media
  const media = ref<MediaAsset[]>([])
  const folders = ref<MediaFolder[]>([])
  const mediaLoaded = ref(false)
  const mediaLoading = ref(false)
  const mediaError = ref<string | null>(null)
  const canManageMedia = ref(false)
  const uploadsEnabled = ref(true)
  const uploads = ref<UploadJob[]>([])

  async function loadMedia(opts: { silent?: boolean } = {}): Promise<void> {
    if (!opts.silent) mediaLoading.value = true
    mediaError.value = null
    try {
      const res = await api().listMedia({})
      media.value = res.items
      folders.value = res.folders
      canManageMedia.value = res.can_manage
      uploadsEnabled.value = res.enabled
      mediaLoaded.value = true
    } catch (e) {
      mediaError.value = errText(e)
    } finally {
      mediaLoading.value = false
    }
  }

  function ensureMedia(): Promise<void> {
    return mediaLoaded.value ? Promise.resolve() : loadMedia()
  }

  function rememberAsset(a: MediaAsset): void {
    const i = media.value.findIndex((m) => m.id === a.id)
    if (i >= 0) media.value.splice(i, 1, a)
    else media.value.unshift(a)
    const f = folders.value.find((x) => x.name === a.folder)
    if (f) f.count = media.value.filter((m) => m.folder === a.folder && !m.deleted).length
    else {
      folders.value.push({ name: a.folder, count: 1 })
      folders.value.sort((x, y) => x.name.localeCompare(y.name, 'th'))
    }
    // answers show the same file: keep their copy fresh
    for (const list of [canned.value, ...Object.values(pickerItems.value)]) {
      for (const r of list) r.media = r.media.map((m) => (m.id === a.id ? a : m))
    }
  }

  /** Upload one prepared file: ticket → PUT to the Worker (+ preview) → register. */
  async function uploadPrepared(p: PreparedFile, opts: { folder: string; convId?: string | null; title?: string }): Promise<MediaAsset | null> {
    const job: UploadJob = {
      id: `u${++jobSeq}`, name: p.name, kind: p.kind, bytes: p.blob.size, progress: 0, status: 'uploading', error: null,
      warnings: [...p.notes], thumb: safeObjectUrl(p.preview ?? (p.kind === 'image' ? p.blob : null)), asset: null,
      convId: opts.convId ?? null,
    }
    uploads.value.push(job)
    const live = () => uploads.value.find((j) => j.id === job.id) ?? job
    try {
      const mime = p.blob.type || (p.kind === 'video' ? 'video/mp4' : 'image/jpeg')
      const t = await api().createMediaUpload({ kind: p.kind, mime, bytes: p.blob.size, sha256: p.sha256, preview: !!p.preview })
      live().warnings.push(...t.warnings)
      let asset: MediaAsset
      if (t.duplicate) {
        asset = t.duplicate
      } else {
        if (!t.token || !t.upload) throw new Error('อัปโหลดไม่สำเร็จ ลองใหม่อีกครั้ง')
        const total = p.blob.size + (p.preview?.size ?? 0)
        await api().putMediaObject(t.upload, p.blob, { onProgress: (sent) => (live().progress = sent / total) })
        if (p.preview && t.preview) {
          await api().putMediaObject(t.preview, p.preview, {
            onProgress: (sent) => (live().progress = (p.blob.size + sent) / total),
          })
        }
        live().status = 'saving'
        asset = await api().registerMedia({
          token: t.token, kind: p.kind, mime, bytes: p.blob.size, title: opts.title ?? p.title, folder: opts.folder,
          width: p.width, height: p.height, duration_ms: p.duration_ms, has_preview: !!p.preview, sha256: p.sha256,
        })
      }
      Object.assign(live(), { status: 'done', progress: 1, asset })
      rememberAsset(asset)
      if (opts.convId) stage(opts.convId, asset, 'manual')
      // a finished chat upload leaves the strip once its chip is staged; library uploads stay listed briefly
      setTimeout(() => dismissUpload(job.id), opts.convId ? 0 : 4000)
      return asset
    } catch (e) {
      Object.assign(live(), { status: 'error', error: errText(e) })
      return null
    }
  }

  /** Files from the file picker / drag & drop / paste: read in the browser, then upload one by one. */
  async function uploadFiles(files: File[], opts: { folder: string; convId?: string | null }): Promise<MediaAsset[]> {
    const done: MediaAsset[] = []
    for (const f of files) {
      let p: PreparedFile
      const pending: UploadJob = {
        id: `u${++jobSeq}`, name: f.name, kind: null, bytes: f.size, progress: 0, status: 'preparing', error: null,
        warnings: [], thumb: null, asset: null, convId: opts.convId ?? null,
      }
      uploads.value.push(pending)
      try {
        p = await prepareFile(f)
      } catch (e) {
        const j = uploads.value.find((x) => x.id === pending.id)
        if (j) Object.assign(j, { status: 'error', error: errText(e) })
        continue
      }
      dismissUpload(pending.id)
      const a = await uploadPrepared(p, opts)
      if (a) done.push(a)
    }
    return done
  }

  function dismissUpload(id: string): void {
    const i = uploads.value.findIndex((j) => j.id === id)
    if (i < 0) return
    const [j] = uploads.value.splice(i, 1)
    if (j?.thumb?.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(j.thumb as string), 2000)
  }

  function uploadsFor(convId: string | null): UploadJob[] {
    return uploads.value.filter((j) => j.convId === convId)
  }

  async function updateMedia(id: string, patch: MediaPatch): Promise<MediaAsset> {
    const a = await api().updateMedia(id, patch)
    rememberAsset(a)
    folders.value = folders.value.filter((f) => media.value.some((m) => m.folder === f.name && !m.deleted))
    for (const f of folders.value) f.count = media.value.filter((m) => m.folder === f.name && !m.deleted).length
    return a
  }

  async function deleteMedia(id: string): Promise<void> {
    await api().deleteMedia(id)
    const gone = media.value.find((m) => m.id === id)
    media.value = media.value.filter((m) => m.id !== id)
    if (gone) {
      const f = folders.value.find((x) => x.name === gone.folder)
      if (f) f.count -= 1
      folders.value = folders.value.filter((x) => x.count > 0)
    }
    for (const list of [canned.value, ...Object.values(pickerItems.value)]) {
      for (const r of list) r.media = r.media.filter((m) => m.id !== id)
    }
    for (const k of Object.keys(staged.value)) unstageAsset(k, id)
  }

  // ------------------------------------------------ answers
  const canned = ref<CannedItem[]>([])
  const cannedLoaded = ref(false)
  const cannedLoading = ref(false)
  const cannedError = ref<string | null>(null)
  const canManageCanned = ref(false)
  const placeholders = ref<Placeholder[]>([])
  /** Every answer type of the suggested script (GET /canned `answer_types`): which ones still have no active answer. */
  const answerTypes = ref<AnswerTypeInfo[]>([])
  /** "/" list per chat: text with that customer's name filled in by the server. */
  const pickerItems = ref<Record<string, CannedItem[]>>({})
  const pickerLoadedAt: Record<string, number> = {}
  const intents = ref<AiIntent[]>([])

  async function loadCanned(opts: { silent?: boolean } = {}): Promise<void> {
    if (!opts.silent) cannedLoading.value = true
    cannedError.value = null
    try {
      const res = await api().listCanned({})
      canned.value = res.items
      canManageCanned.value = res.can_manage
      placeholders.value = res.placeholders
      answerTypes.value = res.answer_types ?? []
      cannedLoaded.value = true
    } catch (e) {
      cannedError.value = errText(e)
    } finally {
      cannedLoading.value = false
    }
  }

  /** Active answers for the "/" picker of one chat (refreshed at most every 30 s). */
  async function loadPicker(convId: string, opts: { force?: boolean } = {}): Promise<CannedItem[]> {
    const fresh = Date.now() - (pickerLoadedAt[convId] ?? 0) < 30_000
    if (!opts.force && fresh && pickerItems.value[convId]) return pickerItems.value[convId] ?? []
    try {
      const res = await api().listCanned({ conversationId: convId })
      pickerItems.value[convId] = pickerList(res.items) // never an answer holding a chat's data ({ค่าส่ง} …)
      pickerLoadedAt[convId] = Date.now()
      if (!placeholders.value.length) placeholders.value = res.placeholders
    } catch {
      /* the picker shows what it had (or nothing) */
    }
    return pickerItems.value[convId] ?? []
  }

  function findCanned(id: string, convId?: string | null): CannedItem | null {
    const lists = [convId ? pickerItems.value[convId] ?? [] : [], canned.value, ...Object.values(pickerItems.value)]
    for (const l of lists) {
      const hit = l.find((r) => r.id === id)
      if (hit) return hit
    }
    return null
  }

  function invalidatePickers(): void {
    for (const k of Object.keys(pickerLoadedAt)) delete pickerLoadedAt[k]
  }

  async function createCanned(body: CannedInput): Promise<CannedItem> {
    const item = await api().createCanned(body)
    canned.value.push(item)
    invalidatePickers()
    return item
  }

  async function updateCanned(id: string, patch: CannedInput): Promise<CannedItem> {
    const item = await api().updateCanned(id, patch)
    const i = canned.value.findIndex((r) => r.id === id)
    if (i >= 0) canned.value.splice(i, 1, item)
    invalidatePickers()
    return item
  }

  async function deleteCanned(id: string): Promise<void> {
    await api().deleteCanned(id)
    canned.value = canned.value.filter((r) => r.id !== id)
    invalidatePickers()
  }

  /** New order of the whole list (drag & drop in the config section). Optimistic, rolled back on error. */
  async function reorderCanned(ids: string[]): Promise<void> {
    const before = [...canned.value]
    const sorts = new Map(before.map((r) => [r, r.sort]))
    const pos = new Map(ids.map((id, i) => [id, i]))
    canned.value = [...canned.value].sort((a, b) => (pos.get(a.id) ?? 1e9) - (pos.get(b.id) ?? 1e9))
    canned.value.forEach((r, i) => (r.sort = (i + 1) * 10))
    try {
      await api().reorderCanned(ids)
      invalidatePickers()
    } catch (e) {
      for (const [r, v] of sorts) r.sort = v // the list on screen sorts by `sort`, not by array order
      canned.value = before
      throw e
    }
  }

  async function loadIntents(): Promise<AiIntent[]> {
    if (!intents.value.length) {
      try {
        intents.value = await api().listIntents()
      } catch {
        /* the editor shows no topic chips */
      }
    }
    return intents.value
  }

  // ------------------------------------------------ files staged in the composer
  const staged = ref<Record<string, StagedFile[]>>({})
  /** Quick-answer pictures the staff removed: do not bring them back for that answer. */
  const quickDismissed = ref<Record<string, string>>({})
  /** Same for the files of the draft (`ai.media`). */
  const draftDismissed = ref<Record<string, string>>({})

  function stagedFor(convId: string): StagedFile[] {
    return staged.value[convId] ?? []
  }

  function stage(convId: string, asset: MediaAsset, source: StagedFile['source']): boolean {
    const list = staged.value[convId] ?? (staged.value[convId] = [])
    if (list.some((s) => s.asset.id === asset.id)) return false
    list.push({ key: `${source}-${asset.id}`, asset, source })
    return true
  }

  function unstage(convId: string, key: string): void {
    const list = staged.value[convId]
    if (!list) return
    const i = list.findIndex((s) => s.key === key)
    if (i < 0) return
    const [gone] = list.splice(i, 1)
    if (gone?.source === 'quick') quickDismissed.value[convId] = `${quickDismissed.value[convId] ?? ''}|${gone.asset.id}`
    if (gone?.source === 'draft') draftDismissed.value[convId] = `${draftDismissed.value[convId] ?? ''}|${gone.asset.id}`
  }

  function unstageAsset(convId: string, assetId: string): void {
    const list = staged.value[convId]
    if (list) staged.value[convId] = list.filter((s) => s.asset.id !== assetId)
  }

  function unstageSource(convId: string, source: StagedFile['source']): void {
    const list = staged.value[convId]
    if (list) staged.value[convId] = list.filter((s) => s.source !== source)
  }

  function toggle(convId: string, asset: MediaAsset): boolean {
    if (stagedFor(convId).some((s) => s.asset.id === asset.id)) {
      unstageAsset(convId, asset.id)
      return false
    }
    return stage(convId, asset, 'manual')
  }

  /** Take everything staged (the composer sends it); `restore` puts it back if the server refused. */
  function takeStaged(convId: string): { assets: MediaAsset[]; restore: () => void } {
    const list = [...stagedFor(convId)]
    staged.value[convId] = []
    return {
      assets: list.map((s) => s.asset),
      restore: () => {
        if (!stagedFor(convId).length) staged.value[convId] = list
      },
    }
  }

  /** Pictures of an answer picked with "/" or a button. */
  async function stageCanned(convId: string, cannedId: string): Promise<number> {
    let item = findCanned(cannedId, convId)
    if (!item) {
      await loadPicker(convId)
      item = findCanned(cannedId, convId)
    }
    let n = 0
    for (const m of item ? sendMediaOf(item) : []) if (stage(convId, m, 'canned')) n++
    return n
  }

  /** Pictures of Jev's quick answer, unless the staff removed them. */
  async function stageQuick(convId: string, cannedId: string): Promise<void> {
    let item = findCanned(cannedId, convId)
    if (!item) {
      await loadPicker(convId)
      item = findCanned(cannedId, convId)
    }
    const dismissed = quickDismissed.value[convId] ?? ''
    for (const m of item ? sendMediaOf(item) : []) if (!dismissed.includes(`|${m.id}`)) stage(convId, m, 'quick')
  }

  function resetQuick(convId: string): void {
    delete quickDismissed.value[convId]
  }

  /** Files that go with the draft (`ai.media`, e.g. the menu round's poster), unless the staff removed them. */
  function stageDraftMedia(convId: string, media: MediaAsset[]): void {
    const dismissed = draftDismissed.value[convId] ?? ''
    for (const m of media) if (!dismissed.includes(`|${m.id}`)) stage(convId, m, 'draft')
  }

  function resetDraftMedia(convId: string): void {
    delete draftDismissed.value[convId]
  }

  return {
    // media
    media, folders, mediaLoaded, mediaLoading, mediaError, canManageMedia, uploadsEnabled, uploads,
    loadMedia, ensureMedia, uploadPrepared, uploadFiles, dismissUpload, uploadsFor, updateMedia, deleteMedia,
    // answers
    canned, cannedLoaded, cannedLoading, cannedError, canManageCanned, placeholders, answerTypes, pickerItems, intents,
    loadCanned, loadPicker, findCanned, createCanned, updateCanned, deleteCanned, reorderCanned, loadIntents,
    // composer
    staged, stagedFor, stage, unstage, unstageAsset, unstageSource, toggle, takeStaged, stageCanned, stageQuick,
    resetQuick, stageDraftMedia, resetDraftMedia,
  }
})

function safeObjectUrl(b: Blob | null): string | null {
  if (!b) return null
  try {
    return URL.createObjectURL(b)
  } catch {
    return null
  }
}
